import { inject, Injectable, NgZone } from '@angular/core';
import { IPageRequest, SortTypes } from '@libs/vc-core-lib';
import { isNumber } from 'lodash-es';
import { debounceTime, fromEvent, Subscription } from 'rxjs';
import { PageRequest } from 'vcp-common-ui-lib';

export interface TableScrollOptions {
    storageKey: string;
    getScrollElement: () => HTMLElement | null;
    getPageNumber: () => number;
    debounceTime?: number;
}

/** Minimal table shape needed to resolve the scrollable container. */
export interface TableWithScrollContainer {
    tableContainer?: { nativeElement?: unknown } | null;
}

export interface TableScrollBindingOptions {
    storageKey: string;
    getTable: () => TableWithScrollContainer | undefined | null;
    getPageNumber: () => number;
    debounceTime?: number;
}

export interface TableScrollBinding {
    start(): void;
    reset(): void;
    restore(): void;
    destroy(): void;
}

/**
 * Shared session-storage fields for search tables.
 * Screen-specific criteria should extend this interface.
 */
export interface TableSessionStateBase<T = void> {
    filterValue?: string;
    pageRequest?: IPageRequest;
    criteria?: T;
    queryParams?: Record<string, string>;
    params?: Record<string, string>;
}

/** Maps persisted `IPageRequest` to table `PageRequest` fields. */
export function toTablePageRequest(pageRequest?: IPageRequest, filterValue?: string): PageRequest | undefined {
    if (!pageRequest) {
        return undefined;
    }
    const sort = pageRequest.sort?.[0];
    const result: PageRequest = {
        pageNumber: pageRequest.pageNumber ?? 0,
        pageSize: pageRequest.pageSize ?? 10,
    };
    if (sort?.fieldName) {
        result.sortField = sort.fieldName;
        result.sortDescending = sort.sort === SortTypes.DESC;
    }
    if (filterValue !== undefined) {
        result.filterValue = filterValue;
    }
    return result;
}

function resolveTableScrollElement(table: TableWithScrollContainer | undefined | null): HTMLElement | null {
    const el = table?.tableContainer?.nativeElement;
    return el instanceof HTMLElement ? el : null;
}

/**
 * Walks up from the table container to the element that actually scrolls. Screens differ: some bound
 * the table height so its container scrolls, others let an ancestor (the page) scroll instead.
 * Falls back to the container while the table is still empty.
 */
function resolveScrollingElement(container: HTMLElement | null): HTMLElement | null {
    const overflow = /auto|scroll|overlay/;
    for (let el = container; el; el = el.parentElement) {
        const style = getComputedStyle(el);
        if (
            (overflow.test(style.overflowY) && el.scrollHeight - el.clientHeight > 1) ||
            (overflow.test(style.overflowX) && el.scrollWidth - el.clientWidth > 1)
        ) {
            return el;
        }
    }
    return container;
}

/**
 * Session-only helpers for search tables: typed JSON state and page-aware scroll.
 * Does not modify TableComponent; callers pass `tableContainer` via `getScrollElement`.
 */
@Injectable({
    providedIn: 'root',
})
export class TableSessionStateService {
    private readonly _ngZone: NgZone = inject(NgZone);

    saveState(storageKey: string, state: unknown): void {
        if (!storageKey) {
            return;
        }
        sessionStorage.setItem(storageKey, JSON.stringify(state));
    }

    /** Reads persisted state from sessionStorage; returns null when missing or invalid. */
    readState<T>(storageKey: string): T | null {
        if (!storageKey) {
            return null;
        }
        const raw = sessionStorage.getItem(storageKey);
        if (raw == null || raw === '') {
            return null;
        }
        try {
            return JSON.parse(raw) as T;
        } catch {
            return null;
        }
    }

    clearState(storageKey: string): void {
        if (!storageKey) {
            return;
        }
        sessionStorage.removeItem(storageKey);
    }

    /**
     * Binds page-aware scroll persist/restore to a `vc-table` (via `getTable`).
     * Listens on `document` in the capture phase so the scrolling ancestor does not have to be known
     * at bind time. Call `destroy()` in `ngOnDestroy`.
     */
    createScrollBinding(options: TableScrollBindingOptions): TableScrollBinding {
        let scrollSub: Subscription | undefined;
        const getScrollElement = (): HTMLElement | null =>
            resolveScrollingElement(resolveTableScrollElement(options.getTable()));
        const scrollOptions = (): TableScrollOptions => ({
            storageKey: options.storageKey,
            getScrollElement,
            getPageNumber: options.getPageNumber,
        });

        return {
            start: (): void => {
                scrollSub?.unsubscribe();
                this._ngZone.runOutsideAngular(() => {
                    scrollSub = fromEvent(document, 'scroll', { capture: true, passive: true })
                        .pipe(debounceTime(options.debounceTime ?? 150))
                        .subscribe((event) => {
                            const scrolling = getScrollElement();
                            if (!(event.target instanceof HTMLElement) || event.target !== scrolling) {
                                return;
                            }
                            sessionStorage.setItem(
                                options.storageKey,
                                JSON.stringify({
                                    scrollTop: event.target.scrollTop,
                                    scrollLeft: event.target.scrollLeft,
                                    pageNumber: options.getPageNumber(),
                                })
                            );
                        });
                });
            },
            reset: (): void => {
                this.resetScrollPosition(options.storageKey, getScrollElement);
            },
            restore: (): void => {
                setTimeout(() => this.restoreScrollPosition(scrollOptions()), 0);
            },
            destroy: (): void => {
                scrollSub?.unsubscribe();
                scrollSub = undefined;
            },
        };
    }

    restoreScrollPosition(options: TableScrollOptions): void {
        const raw = sessionStorage.getItem(options.storageKey);
        if (!raw) {
            return;
        }
        let parsed: { scrollTop?: unknown; scrollLeft?: unknown; pageNumber?: unknown };
        try {
            parsed = JSON.parse(raw) as { scrollTop?: unknown; scrollLeft?: unknown; pageNumber?: unknown };
        } catch {
            return;
        }
        const scrollTop = parsed.scrollTop;
        const scrollLeft = parsed.scrollLeft;
        const pageNumber = parsed.pageNumber;
        if (!isNumber(scrollTop) || !isNumber(scrollLeft) || !isNumber(pageNumber)) {
            return;
        }
        if (pageNumber !== options.getPageNumber()) {
            return;
        }

        const applyScroll = (attemptsRemaining: number): void => {
            const el = options.getScrollElement();
            if (!el) {
                return;
            }
            const maxTop = Math.max(0, el.scrollHeight - el.clientHeight);
            const maxLeft = Math.max(0, el.scrollWidth - el.clientWidth);
            el.scrollTop = Math.min(scrollTop, maxTop);
            el.scrollLeft = Math.min(scrollLeft, maxLeft);

            // Content may still be laying out (e.g. taller Criteria cells); retry until we can
            // reach the saved offset or attempts are exhausted.
            const topShort = scrollTop > 0 && el.scrollTop < scrollTop - 1 && maxTop < scrollTop;
            const leftShort = scrollLeft > 0 && el.scrollLeft < scrollLeft - 1 && maxLeft < scrollLeft;
            if (attemptsRemaining > 0 && (topShort || leftShort)) {
                requestAnimationFrame(() => applyScroll(attemptsRemaining - 1));
            }
        };

        requestAnimationFrame(() => applyScroll(20));
    }

    /** Clears saved scroll and optionally scrolls the live element back to the top. */
    resetScrollPosition(storageKey: string, getScrollElement?: () => HTMLElement | null): void {
        this.clearState(storageKey);
        const el = getScrollElement?.();
        if (el) {
            el.scrollTop = 0;
            el.scrollLeft = 0;
        }
    }
}
